import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { deleteProject, listTasks, type TaskProjectDto } from '../../api/tasks';
import { tProject } from '../../lib/project-i18n';
import { useAuthStore } from '../../store/authStore';

/**
 * v2.9.8 — 삭제 진입점 노출 제어: 생성자 본인만 (서버 SSOT 의 UI 미러).
 * AuthGuard 통과 후 렌더되므로 getState() 시점에 user 는 항상 채워져 있다.
 */
export function isProjectCreator(p: Pick<TaskProjectDto, 'createdBy'>): boolean {
  const me = useAuthStore.getState().user;
  return me != null && String(me.id) === p.createdBy;
}

function errCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
}

/**
 * 프로젝트 삭제 공통 플로우 (v2.3, 2026-06-11) — 내부 내용(할일) 조회 → 존재 여부·개수·
 * 제목 미리보기를 삭제 컨펌(레이어팝업)에 표시 → 확인 시 삭제. 성공 시 true.
 *
 * 권한은 서버가 SSOT (OWNER only) — 403 FORBIDDEN 은 안내 알림으로 처리.
 * 할일 조회 실패 시 목록 응답의 집계(taskCount/doneTaskCount)로 폴백.
 */
export async function confirmAndDeleteProject(p: TaskProjectDto): Promise<boolean> {
  let detail: string;
  try {
    const tasks = await listTasks(p.id);
    if (tasks.length === 0) {
      detail = tProject('project.delete.tasks_none');
    } else {
      const done = tasks.filter((t) => t.status === 'DONE').length;
      let names = tasks.slice(0, 3).map((t) => t.title).join(', ');
      if (tasks.length > 3) names += tProject('project.delete.more_suffix', { n: tasks.length - 3 });
      detail = tProject('project.delete.tasks_summary', { n: tasks.length, done, names });
    }
  } catch {
    detail = tProject('project.delete.tasks_count_only', { n: p.taskCount, done: p.doneTaskCount ?? 0 });
  }

  const ok = await layerConfirm(
    `${tProject('project.delete.confirm', { name: p.name })}\n${detail}`,
    { danger: true },
  );
  if (!ok) return false;

  try {
    await deleteProject(p.id);
    return true;
  } catch (err) {
    const code = errCode(err);
    void layerAlert(code === 'FORBIDDEN_NOT_CREATOR'
      ? tProject('project.delete.err_not_creator')
      : code === 'FORBIDDEN'
        ? tProject('project.delete.err_forbidden')
        : tProject('project.delete.err'));
    return false;
  }
}
